<script setup>
import { computed, onMounted, ref } from 'vue';
import { useI18n } from 'vue-i18n';
import { useAlert } from 'dashboard/composables';
import { picoSearch } from '@scmmishra/pico-search';
import criteriaApi from 'dashboard/api/analysis/criteria';
import SettingsLayout from '../SettingsLayout.vue';
import BaseSettingsHeader from '../components/BaseSettingsHeader.vue';
import {
  BaseTable,
  BaseTableRow,
  BaseTableCell,
} from 'dashboard/components-next/table';
import WootLabel from 'dashboard/components-next/label/Label.vue';
import NextButton from 'dashboard/components-next/button/Button.vue';
import Dialog from 'dashboard/components-next/dialog/Dialog.vue';
import CriterionDialog from './components/CriterionDialog.vue';

const { t } = useI18n();

const records = ref([]);
const isLoading = ref(false);
const searchQuery = ref('');
const selected = ref(null);
const formDialogRef = ref(null);
const deleteDialogRef = ref(null);

const filtered = computed(() => {
  if (!searchQuery.value) return records.value;
  return picoSearch(records.value, searchQuery.value, ['name', 'key']);
});

const tableHeaders = computed(() => [
  t('ANALYSIS.CRITERIA.TABLE.NAME'),
  t('ANALYSIS.CRITERIA.TABLE.KIND'),
  t('ANALYSIS.CRITERIA.TABLE.KEY'),
  t('ANALYSIS.CRITERIA.TABLE.STATUS'),
  t('ANALYSIS.CRITERIA.TABLE.ACTIONS'),
]);

const fetchRecords = async () => {
  isLoading.value = true;
  try {
    const { data } = await criteriaApi.get();
    records.value = data.payload;
  } finally {
    isLoading.value = false;
  }
};

onMounted(fetchRecords);

const openCreate = () => {
  selected.value = null;
  formDialogRef.value.open();
};

const openEdit = record => {
  selected.value = record;
  formDialogRef.value.open();
};

const openDelete = record => {
  selected.value = record;
  deleteDialogRef.value.open();
};

const onSave = async payload => {
  const editing = Boolean(selected.value?.id);
  try {
    if (editing) {
      await criteriaApi.update(selected.value.id, payload);
    } else {
      await criteriaApi.create(payload);
    }
    formDialogRef.value.close();
    useAlert(
      t(
        editing
          ? 'ANALYSIS.CRITERIA.EDIT.SUCCESS'
          : 'ANALYSIS.CRITERIA.CREATE.SUCCESS'
      )
    );
    await fetchRecords();
  } catch (error) {
    const message =
      error?.response?.data?.message ??
      t(
        editing
          ? 'ANALYSIS.CRITERIA.EDIT.ERROR'
          : 'ANALYSIS.CRITERIA.CREATE.ERROR'
      );
    formDialogRef.value.setError(message);
  }
};

const onDelete = async () => {
  try {
    await criteriaApi.delete(selected.value.id);
    useAlert(t('ANALYSIS.CRITERIA.DELETE.SUCCESS'));
    await fetchRecords();
  } catch {
    useAlert(t('ANALYSIS.CRITERIA.DELETE.ERROR'));
  }
};
</script>

<template>
  <SettingsLayout
    :is-loading="isLoading"
    :no-records-found="!filtered.length"
    :no-records-message="t('ANALYSIS.CRITERIA.EMPTY')"
  >
    <template #header>
      <BaseSettingsHeader
        v-model:search-query="searchQuery"
        :title="t('ANALYSIS.CRITERIA.HEADER')"
        :description="t('ANALYSIS.CRITERIA.DESCRIPTION')"
        :search-placeholder="t('ANALYSIS.CRITERIA.SEARCH')"
      >
        <template #actions>
          <NextButton
            icon="i-lucide-plus"
            :label="t('ANALYSIS.CRITERIA.NEW')"
            @click="openCreate"
          />
        </template>
      </BaseSettingsHeader>
    </template>
    <template #preBody>
      <p class="pb-2 text-sm text-n-slate-11">
        {{ t('ANALYSIS.CRITERIA.COST_NOTE') }}
      </p>
    </template>
    <template #body>
      <BaseTable :headers="tableHeaders" :items="filtered">
        <template #row="{ items }">
          <BaseTableRow v-for="record in items" :key="record.id" :item="record">
            <BaseTableCell>
              <div class="flex flex-col">
                <span class="font-medium text-n-slate-12">{{
                  record.name
                }}</span>
                <span class="text-n-slate-11 line-clamp-1">
                  {{ record.instruction }}
                </span>
              </div>
            </BaseTableCell>
            <BaseTableCell>
              {{ t(`ANALYSIS.CRITERIA.KIND.${record.kind.toUpperCase()}`) }}
            </BaseTableCell>
            <BaseTableCell>
              <code class="text-n-slate-11">{{ record.key }}</code>
            </BaseTableCell>
            <BaseTableCell>
              <WootLabel
                :label="
                  record.active
                    ? t('ANALYSIS.CRITERIA.ACTIVE')
                    : t('ANALYSIS.CRITERIA.INACTIVE')
                "
                :color="record.active ? 'teal' : 'slate'"
                compact
              />
            </BaseTableCell>
            <BaseTableCell>
              <div class="flex gap-1">
                <NextButton
                  icon="i-lucide-pencil"
                  slate
                  ghost
                  xs
                  @click="openEdit(record)"
                />
                <NextButton
                  icon="i-lucide-trash-2"
                  ruby
                  ghost
                  xs
                  @click="openDelete(record)"
                />
              </div>
            </BaseTableCell>
          </BaseTableRow>
        </template>
      </BaseTable>
    </template>

    <CriterionDialog
      ref="formDialogRef"
      :criterion="selected"
      @saved="onSave"
    />
    <Dialog
      ref="deleteDialogRef"
      type="alert"
      :title="t('ANALYSIS.CRITERIA.DELETE.TITLE')"
      :description="t('ANALYSIS.CRITERIA.DELETE.MESSAGE')"
      :confirm-button-label="t('ANALYSIS.CRITERIA.DELETE.CONFIRM')"
      :cancel-button-label="t('ANALYSIS.CRITERIA.DELETE.CANCEL')"
      @confirm="onDelete"
    />
  </SettingsLayout>
</template>
